import { useQuery, type ApolloError } from '@apollo/client';
import { isEmpty } from '@newfront-insurance/core';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { useChatOverlayContext } from './chat-overlay-context';
import type { NovaSkill } from '../../../queries/chats';
import { GET_SKILLS_FOR_USER } from '../../../queries/chats';

interface SkillLookup {
  command?: string | null;
  skillId?: string | null;
  name?: string | null;
}

interface SkillsContextValue {
  skills: NovaSkill[];
  isLoading: boolean;
  error?: ApolloError;
  findSkill: (lookup: SkillLookup) => NovaSkill | undefined;
  getSkillDescription: (lookup: SkillLookup) => string | undefined;
  refetchSkills: () => Promise<void>;
}

const SkillsContext = createContext<SkillsContextValue | undefined>(undefined);

function normalizeSkillName(name?: string | null) {
  return name?.trim().toLowerCase() ?? '';
}

function normalizeSkillCommand(command?: string | null) {
  return command?.trim().toLowerCase() ?? '';
}

export function SkillsProvider({ children }: { children: ReactNode }) {
  const { userUuid, accountUuid } = useChatOverlayContext();
  const { data, loading, error, refetch } = useQuery(GET_SKILLS_FOR_USER, {
    variables: { userUuid, accountUuid },
    skip: isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const skills = useMemo<NovaSkill[]>(() => data?.skills ?? [], [data?.skills]);
  const skillsById = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills]);
  const skillsByCommand = useMemo(
    () =>
      new Map(
        skills
          .map((skill) => {
            const command = normalizeSkillCommand(skill.command);
            return command ? ([command, skill] as const) : null;
          })
          .filter((entry): entry is readonly [string, NovaSkill] => entry !== null),
      ),
    [skills],
  );
  const skillsByName = useMemo(() => new Map(skills.map((skill) => [normalizeSkillName(skill.name), skill])), [skills]);

  const findSkill = useCallback(
    ({ command, skillId, name }: SkillLookup) => {
      const normalizedCommand = normalizeSkillCommand(command);
      if (normalizedCommand) {
        const skill = skillsByCommand.get(normalizedCommand);
        if (skill) {
          return skill;
        }
      }

      if (skillId) {
        const skill = skillsById.get(skillId);
        if (skill) {
          return skill;
        }
      }

      const normalizedName = normalizeSkillName(name);
      return normalizedName ? skillsByName.get(normalizedName) : undefined;
    },
    [skillsByCommand, skillsById, skillsByName],
  );

  const getSkillDescription = useCallback(
    (lookup: SkillLookup) => findSkill(lookup)?.description?.trim() || undefined,
    [findSkill],
  );

  // Apollo's refetch ignores skip, so hosts without a user (Benji) would fetch skills.
  const refetchSkills = useCallback(async () => {
    if (!isEmpty(userUuid)) {
      await refetch();
    }
  }, [refetch, userUuid]);

  const value = useMemo<SkillsContextValue>(
    () => ({
      skills,
      isLoading: loading,
      error: error ?? undefined,
      findSkill,
      getSkillDescription,
      refetchSkills,
    }),
    [skills, loading, error, findSkill, getSkillDescription, refetchSkills],
  );

  return <SkillsContext.Provider value={value}>{children}</SkillsContext.Provider>;
}

export function useSkills(): SkillsContextValue {
  const context = useContext(SkillsContext);
  if (!context) {
    throw new Error('useSkills must be used within SkillsProvider');
  }
  return context;
}

export function useSkillsSafe() {
  return useContext(SkillsContext);
}
